import azureDevOpsService from './azureDevOpsService';

function authHeaders() {
  const pat = azureDevOpsService.getPat();
  return pat ? { 'X-Azure-Devops-Pat': pat } : {};
}

const SLACK_CHANNEL = '#qa-test-notif';

const rateEmoji = (rate) => {
  const n = parseFloat(rate);
  if (isNaN(n)) return '⚪';
  if (n >= 95) return '🟢';
  if (n >= 85) return '🟡';
  return '🔴';
};

const statusEmoji = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('stable') && !l.includes('pas')) return '🟢';
  if (l.includes('warning') || l.includes('partiel')) return '🟡';
  return '🔴';
};

const fmtRate = (rate) => `${parseFloat(rate || 0).toFixed(1)}%`;

/**
 * Construit les Slack Block Kit blocks pour le récapitulatif QA
 * @param {Object} data
 *   version, globalRate, globalStatusLabel, totalTests, totalPassed, totalFailed,
 *   squads: [{name, successRate, totalTests, passedTests, failedTests}],
 *   bugs: {total, critical, high, medium, low}
 */
const buildQARecapBlocks = (data) => {
  const {
    version,
    globalRate,
    globalStatusLabel,
    totalTests,
    totalPassed,
    totalFailed,
    squads = [],
    bugs,
  } = data;

  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const globalEmoji = statusEmoji(globalStatusLabel);
  const globalRateEmoji = rateEmoji(globalRate);

  const blocks = [];

  // ── Header ─────────────────────────────────────────────
  blocks.push({
    type: 'header',
    text: { type: 'plain_text', text: '📊 QA Dashboard — Vue Globale', emoji: true },
  });

  // ── Version + date ─────────────────────────────────────
  blocks.push({
    type: 'context',
    elements: [{
      type: 'mrkdwn',
      text: `Version *${version}*  ·  ${dateStr}  ·  ${timeStr}`,
    }],
  });

  blocks.push({ type: 'divider' });

  // ── Statut global ──────────────────────────────────────
  blocks.push({
    type: 'section',
    fields: [
      {
        type: 'mrkdwn',
        text: `*Statut global*\n${globalEmoji} ${globalStatusLabel || 'N/A'}`,
      },
      {
        type: 'mrkdwn',
        text: `*Taux de réussite*\n${globalRateEmoji} *${fmtRate(globalRate)}*`,
      },
    ],
  });

  // ── Stats totales ──────────────────────────────────────
  blocks.push({
    type: 'section',
    fields: [
      {
        type: 'mrkdwn',
        text: `*Tests exécutés*\n📊 ${(totalTests || 0).toLocaleString('fr-FR')}`,
      },
      {
        type: 'mrkdwn',
        text: `*Résultats*\n✅ ${(totalPassed || 0).toLocaleString('fr-FR')} passés  ·  ❌ ${(totalFailed || 0).toLocaleString('fr-FR')} échoués`,
      },
    ],
  });

  // ── Tableau par squad ──────────────────────────────────
  if (squads.length > 0) {
    blocks.push({ type: 'divider' });
    blocks.push({
      type: 'section',
      text: { type: 'mrkdwn', text: '*📋 Résultats par squad*' },
    });

    // 2 squads par ligne (champ fields = 2 colonnes dans Slack)
    for (let i = 0; i < squads.length; i += 2) {
      const fields = [squads[i], squads[i + 1]].filter(Boolean).map(sq => {
        const rate = parseFloat(sq.successRate || 0);
        const emoji = rateEmoji(rate);
        const passed = (sq.passedTests || 0).toLocaleString('fr-FR');
        const failed = (sq.failedTests || 0).toLocaleString('fr-FR');
        const total = (sq.totalTests || 0).toLocaleString('fr-FR');
        return {
          type: 'mrkdwn',
          text: `${emoji} *${sq.name}*\n${fmtRate(rate)}  ·  ✅${passed} ❌${failed} / ${total}`,
        };
      });
      blocks.push({ type: 'section', fields });
    }
  }

  // ── Bugs ───────────────────────────────────────────────
  if (bugs && bugs.total !== undefined) {
    blocks.push({ type: 'divider' });

    const bugParts = [`🐛 *${bugs.total} bug${bugs.total !== 1 ? 's' : ''} actifs*`];
    const details = [];
    if (bugs.critical) details.push(`🔴 ${bugs.critical} critique${bugs.critical > 1 ? 's' : ''}`);
    if (bugs.high) details.push(`🟠 ${bugs.high} majeur${bugs.high > 1 ? 's' : ''}`);
    if (bugs.medium) details.push(`🟡 ${bugs.medium} moyen${bugs.medium > 1 ? 's' : ''}`);
    if (bugs.low) details.push(`⚪ ${bugs.low} mineur${bugs.low > 1 ? 's' : ''}`);
    if (details.length) bugParts.push(details.join('  ·  '));

    blocks.push({
      type: 'section',
      text: { type: 'mrkdwn', text: bugParts.join('\n') },
    });
  }

  blocks.push({ type: 'divider' });

  // ── Footer ─────────────────────────────────────────────
  blocks.push({
    type: 'context',
    elements: [{
      type: 'mrkdwn',
      text: `🤖 _Envoyé automatiquement par QA Dashboard · ${dateStr} à ${timeStr}_`,
    }],
  });

  return blocks;
};

/**
 * Envoie le récapitulatif QA vers Slack avec screenshot
 */
export const sendDashboardToSlack = async (dashboardData, screenshotBlob) => {
  const blocks = buildQARecapBlocks(dashboardData);

  let screenshotBase64 = null;
  if (screenshotBlob) {
    screenshotBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(screenshotBlob);
    });
  }

  const response = await fetch('/api/slack/send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      channel: SLACK_CHANNEL,
      blocks,
      text: `📊 QA Dashboard — Vue Globale · Version ${dashboardData.version} · ${fmtRate(dashboardData.globalRate)}`,
      screenshot: screenshotBase64,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Erreur serveur (${response.status})`);
  }

  return {
    success: true,
    message: '✅ Récapitulatif envoyé vers Slack' + (screenshotBase64 ? ' avec screenshot' : ''),
    channel: SLACK_CHANNEL,
  };
};

export const testSlackConnection = async () => {
  try {
    const response = await fetch('/api/slack/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        channel: SLACK_CHANNEL,
        text: '✅ Test connexion QA Dashboard',
        blocks: [],
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
};
